import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogContentText, makeStyles } from '@material-ui/core';
import RateCardIcon from '@material-ui/icons/AttachMoneySharp';
import {
  EditableCard2,
  Edit,
  EditContent,
  EditTitle,
  ReadOnly,
  ReadOnlyContent
} from '~/modules/common/components/EditableCard';
import { RemoveEntityConfirmationDialog } from '~/modules/common/components';
import { useDialogState } from '~/modules/common/hooks';
import {
  useAvailableCostOverrideDimensions,
  useProjectCostOverrideDimensionHandlers,
  useProjectCostOverrideErrors,
  usePutProjectCostRates,
  useUpdateProjectCostRates,
  mapCostOverrideRates
} from '../hooks';
import { ProjectCostOverridesActionComponent } from './ProjectCostOverridesActionComponent';
import { ProjectCostOverridesDialogContent } from './ProjectCostOverridesDialogContent';
import { ProjectCostOverridesReadOnlyContent } from './ProjectCostOverridesReadOnlyContent';

const avatar = <RateCardIcon />;

const useCardStyles = makeStyles(theme => ({
  card: {
    marginTop: theme.spacing(2)
  }
}));

const useEditContentStyles = makeStyles({
  noVerticalPadding: {
    paddingTop: 0,
    paddingBottom: 0
  }
});

const useDialogActionStyles = makeStyles(theme => ({
  dialogActions: {
    display: 'block',
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3)
  }
}));

const getEditMaxWidth = (selectedDimensions, projectRates) => {
  const dimensionsCount = selectedDimensions?.length || 0;
  const hasEffectiveDate = projectRates.some(r => !r.isInitialRate);

  if (dimensionsCount > 2) return 'xl';
  if (dimensionsCount > 1) return 'lg';
  if (dimensionsCount === 0 && hasEffectiveDate) return 'md';
  if (dimensionsCount === 0) return 'sm';

  return 'md';
};

const getReadOnlyMaxWidth = (selectedDimensions, projectRates) => {
  const dimensionsCount = selectedDimensions?.length || 0;
  const hasEffectiveDate = projectRates.some(r => !r.isInitialRate);

  if (dimensionsCount > 2) return 'lg';
  if (dimensionsCount > 1) return 'md';
  if (dimensionsCount === 0 && hasEffectiveDate) return 'sm';
  if (dimensionsCount === 0) return 'sm';

  return 'sm';
};

const getMaxWidth = (selectedDimensions, projectRates, viewSummary) =>
  viewSummary
    ? getReadOnlyMaxWidth(selectedDimensions, projectRates)
    : getEditMaxWidth(selectedDimensions, projectRates);

const areDimensionsUnchanged = (initialDimensions, selectedDimensions) =>
  initialDimensions.length === selectedDimensions.length &&
  selectedDimensions.every(({ id }, i) => initialDimensions[i]?.id === id);

export const ProjectCostOverridesEditable = ({
  editable = false,
  projectSlug,
  projectUri,
  projectCurrency,
  projectRates = [],
  projectRateCount = 0,
  projectDimensions = [],
  hasMore = false,
  loadingMore = false,
  loadMore,
  reloadAll,
  edit = false,
  viewSummary = false,
  onViewSummaryClick,
  onCancelCallback
}) => {
  const { formatMessage } = useIntl();
  const cardClasses = useCardStyles();
  const editContentClasses = useEditContentStyles();
  const dialogActionClasses = useDialogActionStyles();

  const transformedRates = useMemo(
    () => mapCostOverrideRates(projectRates, projectDimensions),
    [projectRates, projectDimensions]
  );

  const editor = useProjectCostOverrideDimensionHandlers({
    projectCurrency,
    initialSelectedDimensions: projectDimensions,
    initialProjectRates: transformedRates,
    hasMore,
    loadMore
  });

  const { availableDimensions } = useAvailableCostOverrideDimensions({
    selectedDimensions: editor.selectedDimensions
  });

  const { putProjectCostRates, isSaving: isPutting } = usePutProjectCostRates();
  const {
    updateProjectCostRates,
    isSaving: isUpdating
  } = useUpdateProjectCostRates();
  const isSaving = isPutting || isUpdating;

  const { open: dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const [pendingGroupId, setPendingGroupId] = useState(null);

  const openDeleteDialog = useCallback(
    groupId => {
      setPendingGroupId(groupId);
      openDialog();
    },
    [openDialog]
  );

  const openRemoveAllDialog = useCallback(() => {
    setPendingGroupId(null);
    openDialog();
  }, [openDialog]);

  const onCloseDialog = useCallback(() => {
    setPendingGroupId(null);
    closeDialog();
  }, [closeDialog]);

  const hasDimensionsWithoutRates =
    editor.selectedDimensions.length > 0 &&
    editor.projectRates.length === 0 &&
    editor.persistedRates.length === 0;

  const {
    selectedDimensions: editorSelectedDimensions,
    projectRates: editorProjectRates,
    beginSave: editorBeginSave,
    adoptServerRates: editorAdoptServerRates,
    formOnChange: editorFormOnChange,
    getRatesForSave: editorGetRatesForSave
  } = editor;

  const onAddCostOverride = useCallback(
    () => editorFormOnChange.onAddEntryClick(null),
    [editorFormOnChange]
  );

  const onConfirmRemove = useCallback(async () => {
    if (pendingGroupId) {
      editorFormOnChange.onDeleteGroup(pendingGroupId);
      onCloseDialog();

      return;
    }

    if (isSaving) return;
    editorBeginSave();
    await putProjectCostRates({
      projectId: projectUri,
      selectedDimensions: [],
      projectRates: []
    });
    await reloadAll();
    editorFormOnChange.onRemoveAll();
    onCloseDialog();
  }, [
    editorBeginSave,
    editorFormOnChange,
    isSaving,
    onCloseDialog,
    pendingGroupId,
    projectUri,
    putProjectCostRates,
    reloadAll
  ]);

  const { errors, hasErrors } = useProjectCostOverrideErrors({
    selectedDimensions: editorSelectedDimensions,
    projectRates: editorProjectRates,
    formatMessage
  });

  const onSave = useCallback(async () => {
    if (isSaving || hasErrors) return;

    const dimensionsUnchanged = areDimensionsUnchanged(
      projectDimensions,
      editorSelectedDimensions
    );

    // A dimension change saves via putProjectCostRates, which fully replaces the
    // schedule, so every page must be loaded first or unloaded rows would be
    // dropped. An in-place edit saves via updateProjectCostRates and only needs
    // the loaded rows sent, matching the Rate Card save flow.
    if (!dimensionsUnchanged && hasMore) {
      await loadMore(true);
    }

    const {
      selectedDimensions: saveDimensions,
      projectRates: saveRates,
      persistedRates: savePersistedRates
    } = editorGetRatesForSave();

    const saveCostRates = dimensionsUnchanged
      ? updateProjectCostRates
      : putProjectCostRates;

    editorBeginSave();
    await saveCostRates({
      projectId: projectUri,
      selectedDimensions: saveDimensions,
      projectRates: saveRates,
      persistedRates: savePersistedRates
    });
    const reloadedRates = await reloadAll();

    editorAdoptServerRates(
      mapCostOverrideRates(reloadedRates, editorSelectedDimensions)
    );
  }, [
    editorAdoptServerRates,
    editorBeginSave,
    editorGetRatesForSave,
    editorSelectedDimensions,
    hasErrors,
    hasMore,
    isSaving,
    loadMore,
    projectDimensions,
    projectUri,
    putProjectCostRates,
    reloadAll,
    updateProjectCostRates
  ]);

  const maxWidth = getMaxWidth(
    editor.selectedDimensions,
    editor.projectRates,
    viewSummary
  );
  const isEditable = viewSummary ? false : editable;

  const handleCancel = onCancelCallback || editor.reset;
  const titleId = viewSummary
    ? 'projectCostOverrides.summary.cardTitle'
    : 'projectCostOverrides.cardTitle';

  const actionComponentCustomProps = useMemo(
    () => ({
      readOnly: viewSummary,
      selectedDimensions: editor.selectedDimensions,
      onAddCostOverride,
      isLocked: loadingMore
    }),
    [viewSummary, editor.selectedDimensions, onAddCostOverride, loadingMore]
  );

  const resourceKeys = pendingGroupId
    ? {
        header: 'projectCostOverrides.removeCostOverride.header',
        actionButton: 'projectCostOverrides.removeCostOverride.actionButton'
      }
    : {
        header: 'projectCostOverrides.removeAllCostOverrides.header',
        actionButton: 'projectCostOverrides.removeAllCostOverrides.actionButton'
      };

  return (
    <>
      <EditableCard2
        editable={isEditable}
        edit={edit}
        classes={cardClasses}
        editContentClassName={editContentClasses.noVerticalPadding}
        dialogActionClasses={dialogActionClasses}
        maxWidth={maxWidth}
        ariaLabelKey="projectCostOverrides.cardTitle"
        dataQeId="ProjectCostOverridesCard"
      >
        <ReadOnly
          expandable={false}
          avatar={avatar}
          title={formatMessage({ id: 'projectCostOverrides.cardTitle' })}
        >
          <ReadOnlyContent>
            <ProjectCostOverridesReadOnlyContent
              projectRateCount={projectRateCount}
              projectDimensions={projectDimensions}
              onViewSummary={onViewSummaryClick}
            />
          </ReadOnlyContent>
        </ReadOnly>
        <Edit
          onSave={onSave}
          onCancel={handleCancel}
          onRemove={
            !viewSummary && projectRateCount > 0
              ? openRemoveAllDialog
              : undefined
          }
          saveable={
            !isSaving &&
            editor.isDirty &&
            !hasDimensionsWithoutRates &&
            !hasErrors
          }
          ActionComponent={ProjectCostOverridesActionComponent}
          actionComponentCustomProps={actionComponentCustomProps}
        >
          <EditTitle>{formatMessage({ id: titleId })}</EditTitle>
          <EditContent>
            <ProjectCostOverridesDialogContent
              projectSlug={projectSlug}
              projectCurrency={projectCurrency}
              editor={editor}
              availableDimensions={availableDimensions}
              errors={errors}
              hasMore={hasMore}
              loadingMore={loadingMore}
              loadMore={loadMore}
              openDeleteDialog={openDeleteDialog}
              readOnly={viewSummary}
            />
          </EditContent>
        </Edit>
      </EditableCard2>
      {dialogOpen && (
        <RemoveEntityConfirmationDialog
          resourceKeys={resourceKeys}
          open={dialogOpen}
          onClose={onCloseDialog}
          onConfirm={onConfirmRemove}
          isRemoving={!pendingGroupId && isSaving}
          disabled={!pendingGroupId && isSaving}
        >
          <DialogContentText>
            <FormattedMessage
              id={
                pendingGroupId
                  ? 'projectCostOverrides.removeCostOverride.message'
                  : 'projectCostOverrides.removeAllCostOverrides.message'
              }
            />
          </DialogContentText>
        </RemoveEntityConfirmationDialog>
      )}
    </>
  );
};

ProjectCostOverridesEditable.propTypes = {
  editable: PropTypes.bool,
  projectSlug: PropTypes.string,
  projectUri: PropTypes.string,
  projectCurrency: PropTypes.object,
  projectRates: PropTypes.array,
  projectRateCount: PropTypes.number,
  projectDimensions: PropTypes.array,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  loadMore: PropTypes.func,
  reloadAll: PropTypes.func,
  edit: PropTypes.bool,
  viewSummary: PropTypes.bool,
  onViewSummaryClick: PropTypes.func,
  onCancelCallback: PropTypes.func
};

export default ProjectCostOverridesEditable;
